import { useState } from 'react';
import { Search, Bell, LogOut, Menu, X, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/store/uiStore';

interface HeaderProps {
  breadcrumbs: { label: string; path?: string }[];
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Administrador',
  admin: 'Administrador',
  viewer: 'Visualizador',
};

export function Header({ breadcrumbs }: HeaderProps) {
  const { manager, logout } = useAuth();
  const openSidebar = useUIStore((s) => s.openSidebar);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const notificationCount = 3;

  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const handleLogout = async () => {
    if (confirm('Deseja realmente sair do painel?')) {
      await logout();
    }
  };

  const avatarSeed = manager?.name || 'Admin';
  const avatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${avatarSeed}`;
  const lastCrumb = breadcrumbs[breadcrumbs.length - 1];

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 bg-primary px-3 shadow-sm sm:h-16 sm:gap-3 sm:px-6">
      {/* Hamburger (mobile/tablet) */}
      <button
        onClick={openSidebar}
        aria-label="Abrir menu"
        className="flex size-10 shrink-0 items-center justify-center rounded-lg text-white hover:bg-card/10 lg:hidden"
      >
        <Menu size={22} />
      </button>

      {/* Breadcrumbs - compact on mobile */}
      <div className="flex min-w-0 flex-1 items-center gap-2 lg:flex-initial">
        {/* Mobile: only show last crumb */}
        <span className="truncate text-sm font-semibold text-white sm:hidden">
          {lastCrumb?.label}
        </span>
        {/* Tablet+: full breadcrumbs */}
        <nav aria-label="Breadcrumb" className="hidden items-center gap-2 sm:flex">
          {breadcrumbs.map((crumb, index) => (
            <div key={index} className="flex items-center gap-2">
              {crumb.path ? (
                <Link
                  to={crumb.path}
                  className="text-sm font-medium text-white/80 transition-colors hover:text-white"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-sm font-semibold text-white">{crumb.label}</span>
              )}
              {index < breadcrumbs.length - 1 && (
                <span className="text-sm text-white/60">/</span>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Right Side */}
      <div className="flex items-center justify-end gap-1 sm:gap-3 lg:flex-1">
        {/* Search - inline on lg+ */}
        <label className="relative hidden lg:block lg:w-64 xl:w-80">
          <Search
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/60"
          />
          <input
            className="h-10 w-full rounded-lg border border-white/20 bg-card/10 pl-10 pr-3 text-sm text-white placeholder:text-white/60 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
            placeholder="Busca global..."
            aria-label="Busca global"
          />
        </label>

        {/* Search toggle - mobile/tablet */}
        <button
          onClick={() => setMobileSearchOpen((v) => !v)}
          aria-label="Buscar"
          className="flex size-10 items-center justify-center rounded-lg text-white hover:bg-card/10 lg:hidden"
        >
          {mobileSearchOpen ? <X size={20} /> : <Search size={20} />}
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          aria-label={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          title={isDark ? 'Tema claro' : 'Tema escuro'}
          className="flex size-10 shrink-0 items-center justify-center rounded-lg text-white transition-colors hover:bg-card/10"
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* Notifications */}
        <button
          aria-label={`Notificações${notificationCount ? ` (${notificationCount})` : ''}`}
          className="relative flex size-10 shrink-0 items-center justify-center rounded-lg text-white transition-colors hover:bg-card/10"
        >
          <Bell size={20} />
          {notificationCount > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold tabular-nums text-white ring-2 ring-primary">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </button>

        {/* User */}
        <div className="flex items-center gap-2 sm:gap-3">
          <img
            src={avatar}
            alt=""
            className="size-9 shrink-0 rounded-full bg-card/20 sm:size-10"
          />
          <div className="hidden flex-col text-sm leading-tight md:flex">
            <p className="font-semibold text-white">{manager?.name || 'Administrador'}</p>
            <p className="text-xs text-white/70">
              {manager?.role ? roleLabels[manager.role] : 'Admin'}
            </p>
          </div>
          <button
            onClick={handleLogout}
            aria-label="Sair do painel"
            title="Sair do painel"
            className="flex size-10 shrink-0 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-card/10 hover:text-white"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>

      {/* Mobile/tablet search drawer */}
      {mobileSearchOpen && (
        <div className="absolute inset-x-0 top-full border-t border-white/10 bg-primary px-3 py-2 shadow-md lg:hidden">
          <label className="relative block">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/60"
            />
            <input
              autoFocus
              className="h-10 w-full rounded-lg border border-white/20 bg-card/10 pl-10 pr-3 text-sm text-white placeholder:text-white/60 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
              placeholder="Busca global..."
              aria-label="Busca global"
            />
          </label>
        </div>
      )}
    </header>
  );
}
